import { Box, Button, Input, Text } from '@bonanza-social/moonlight';
import { StackNavigationProp, useNavigation } from '@bonanza-social/navigation';
import {
  ElementRef,
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from '@bonanza-social/i18n';
import { useDispatch, useSelector } from '@bonanza-social/toolkit/state';
import { changePassword, selectChangePasswordDataState } from '../../state';
import { ChangePasswordErrors } from './ChangePassword.types';
import { apiConfig } from '@bonanza-social/toolkit/api';
import { InteractionManager } from 'react-native';

function ChangePassword() {
  const { t } = useTranslation('auth');
  const dispatch = useDispatch();
  const navigation =
    useNavigation<StackNavigationProp<AppNavigatorParamsList>>();
  const changePasswordState = useSelector(selectChangePasswordDataState);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState('');
  const [errors, setErrors] = useState<ChangePasswordErrors>({});

  const currentPasswordRef = useRef<ElementRef<typeof Input>>(null);
  const newPasswordRef = useRef<ElementRef<typeof Input>>(null);
  const newPasswordConfirmationRef = useRef<ElementRef<typeof Input>>(null);

  const validatePasswordLength = useCallback(
    (password: string) => {
      return password.length >= 8 ? undefined : t('passwordMinLengthError');
    },
    [t],
  );

  const validatePasswordMatch = useCallback(
    (password: string, confirmPassword: string) => {
      return password === confirmPassword
        ? undefined
        : t('passwordMismatchError');
    },
    [t],
  );

  const validateNewPasswordDifferent = useCallback(
    (newPassword: string, currentPassword: string) => {
      return newPassword !== currentPassword
        ? undefined
        : t('passwordSameAsCurrentError');
    },
    [t],
  );

  const handleCurrentPasswordChange = useCallback(
    (value: string) => {
      setCurrentPassword(value);

      const updatedErrors = {
        ...errors,
        currentPassword: undefined,
        submitError: undefined,
      };

      if (newPassword && value && newPassword === value) {
        updatedErrors.newPassword = t('passwordSameAsCurrentError');
      } else if (
        newPassword &&
        errors.newPassword === t('passwordSameAsCurrentError')
      ) {
        updatedErrors.newPassword = undefined;
      }

      setErrors(updatedErrors);
    },
    [errors, newPassword, t],
  );

  const handleNewPasswordChange = useCallback(
    (value: string) => {
      setNewPassword(value);

      if (!value) {
        setErrors((prev) => ({
          ...prev,
          newPassword: undefined,
          submitError: undefined,
        }));
        return;
      }

      const lengthError = validatePasswordLength(value);
      const sameAsCurrentError =
        value && currentPassword
          ? validateNewPasswordDifferent(value, currentPassword)
          : undefined;
      const matchError =
        value && newPasswordConfirmation
          ? validatePasswordMatch(value, newPasswordConfirmation)
          : undefined;

      setErrors((prev) => ({
        ...prev,
        newPassword: lengthError || sameAsCurrentError,
        newPasswordConfirmation: matchError,
        submitError: undefined,
      }));
    },
    [
      currentPassword,
      newPasswordConfirmation,
      validateNewPasswordDifferent,
      validatePasswordLength,
      validatePasswordMatch,
    ],
  );

  const handleNewPasswordConfirmationChange = useCallback(
    (value: string) => {
      setNewPasswordConfirmation(value);

      if (!value) {
        setErrors((prev) => ({
          ...prev,
          newPassword: undefined,
          newPasswordConfirmation: undefined,
          submitError: undefined,
        }));
        return;
      }

      const lengthError = validatePasswordLength(value);
      const matchError = newPassword
        ? validatePasswordMatch(newPassword, value)
        : undefined;

      setErrors((prev) => ({
        ...prev,
        newPasswordConfirmation: lengthError || matchError,
        submitError: undefined,
      }));
    },
    [newPassword, validatePasswordLength, validatePasswordMatch],
  );

  const onCurrentPasswordPress = useCallback(() => {
    newPasswordRef.current?.focus();
  }, []);

  const onNewPasswordPress = useCallback(() => {
    newPasswordConfirmationRef.current?.focus();
  }, []);

  const onUpdatePress = useCallback(async () => {
    try {
      const userId = await apiConfig.getUserId();
      await dispatch(
        changePassword({
          accountId: userId || '',
          currentPassword,
          newPassword,
        }),
      ).unwrap();

      requestAnimationFrame(() => {
        navigation.goBack();
      });
    } catch (_) {
      setErrors((prev) => ({
        ...prev,
        submitError: t('changePasswordGenericError'),
      }));
    }
  }, [currentPassword, dispatch, navigation, newPassword, t]);

  const isSubmitDisabled = useMemo(() => {
    return Boolean(
      errors.newPassword ||
        errors.newPasswordConfirmation ||
        errors.submitError ||
        !currentPassword ||
        !newPassword ||
        !newPasswordConfirmation,
    );
  }, [
    currentPassword,
    errors.newPassword,
    errors.newPasswordConfirmation,
    errors.submitError,
    newPassword,
    newPasswordConfirmation,
  ]);

  const onCancelPress = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  return (
    <Box
      flex={1}
      justifyContent="space-between"
      marginTop="space-200"
      gap="space-400"
    >
      <Box gap="space-600">
        <Text
          variant="h4"
          gradient="darkSurfaceAccent"
          locNS="auth"
          locKey="changePasswordTitle"
        />
        <Input
          ref={currentPasswordRef}
          placeholderKey="currentPassword"
          placeholderNS="auth"
          autoComplete="password"
          enterKeyHint="next"
          value={currentPassword}
          onChangeText={handleCurrentPasswordChange}
          onSubmitEditing={onCurrentPasswordPress}
          secureTextEntry
        />
        <Input
          ref={newPasswordRef}
          placeholderKey="newPassword"
          placeholderNS="auth"
          enterKeyHint="next"
          autoComplete="new-password"
          value={newPassword}
          onChangeText={handleNewPasswordChange}
          onSubmitEditing={onNewPasswordPress}
          secureTextEntry
          error={errors.newPassword}
        />
        {errors.newPassword ? (
          <Text
            color="darkSurfaceError"
            variant="body-m"
            marginTop="-space-200"
          >
            {errors.newPassword}
          </Text>
        ) : null}
        <Input
          ref={newPasswordConfirmationRef}
          placeholderKey="newPasswordConfirmation"
          placeholderNS="auth"
          enterKeyHint="go"
          autoComplete="new-password"
          value={newPasswordConfirmation}
          onChangeText={handleNewPasswordConfirmationChange}
          onSubmitEditing={onUpdatePress}
          secureTextEntry
          error={errors.newPasswordConfirmation}
        />
        {errors.newPasswordConfirmation || errors.submitError ? (
          <Text
            color="darkSurfaceError"
            variant="body-m"
            marginTop="-space-200"
          >
            {errors.newPasswordConfirmation || errors.submitError}
          </Text>
        ) : null}
      </Box>
      <Box flexDirection="column" gap="space-400">
        <Button
          variant="primary"
          size="large"
          locNS="auth"
          locKey="changePasswordUpdateButton"
          onPress={onUpdatePress}
          loading={changePasswordState === 'pending'}
          disabled={isSubmitDisabled}
        />
        <Button
          variant="secondary"
          size="large"
          locNS="auth"
          locKey="changePasswordCancelButton"
          onPress={onCancelPress}
        />
      </Box>
    </Box>
  );
}

export default memo(ChangePassword);
